/**
 * textChunker — Markdown stripping and sentence-level text chunking for TTS.
 *
 * Two exports:
 *   - stripMarkdown(text): Removes all markdown formatting, returns plain text.
 *   - chunkText(text): Strips markdown, splits into sentence-boundary chunks
 *     capped at 500 chars each. Returns array of non-empty trimmed strings.
 *
 * Design decisions (from CONTEXT.md):
 *   - Code blocks removed entirely (speaking code aloud is useless)
 *   - Bullet lists treated as continuous prose — no "first, second" markers
 *   - Chunks capped at 500 chars, split at nearest sentence boundary
 */

/**
 * Remove all markdown formatting from text, returning speakable plain text.
 *
 * Transformations (in order):
 *   1. Multi-line code blocks (```...```) — removed entirely
 *   2. Inline `code` — removed entirely
 *   3. Images (![alt](url)) — removed entirely
 *   4. Links ([text](url)) — keep display text, remove URL
 *   5. Bold/italic (***text***, **text**, *text*, ___text___, __text__, _text_) — keep text
 *   6. Headers (# to ######) — remove marker, keep text
 *   7. Blockquotes (> text) — remove marker, keep text
 *   8. Normalize excess whitespace
 *
 * @param {string} text - Raw text that may contain markdown.
 * @returns {string} Plain text suitable for speech synthesis.
 */
function stripMarkdown(text) {
  if (!text || typeof text !== 'string') return '';

  return text
    // 1. Remove multi-line code blocks entirely (must come before inline code)
    .replace(/```[\s\S]*?```/g, '')
    // 2. Remove inline code entirely
    .replace(/`[^`]+`/g, '')
    // 3. Remove images entirely (must come before links)
    .replace(/!\[[^\]]*\]\([^)]+\)/g, '')
    // 4. Remove links — keep display text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // 5. Remove bold/italic markers — keep text (triple, double, single; * and _)
    .replace(/\*{1,3}([^*\n]+)\*{1,3}/g, '$1')
    .replace(/_{1,3}([^_\n]+)_{1,3}/g, '$1')
    // 6. Remove header markers (# at start of line)
    .replace(/^#{1,6}\s+/gm, '')
    // 7. Remove blockquote markers (> at start of line)
    .replace(/^>\s+/gm, '')
    // 8. Normalize whitespace — collapse 3+ newlines to 2, trim
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Split text into sentence-level chunks suitable for TTS synthesis.
 *
 * Process:
 *   1. Strip markdown via stripMarkdown()
 *   2. Split on sentence boundaries (.!? followed by whitespace)
 *   3. Accumulate sentences into chunks, flushing when adding the next
 *      sentence would exceed 500 chars
 *   4. Return array of non-empty trimmed strings
 *
 * Bullet lists are treated as continuous prose — no special handling.
 * Empty or whitespace-only input returns an empty array.
 *
 * @param {string} text - Raw text (may contain markdown).
 * @returns {string[]} Array of non-empty, trimmed text chunks.
 */
function chunkText(text) {
  if (!text || typeof text !== 'string') return [];

  const clean = stripMarkdown(text);
  if (!clean) return [];

  // Split on sentence-ending punctuation followed by whitespace
  // Using lookbehind so the punctuation stays with the sentence
  const sentences = clean.split(/(?<=[.!?])\s+/);

  const chunks = [];
  let current = '';

  for (const sentence of sentences) {
    const trimmed = sentence.trim();
    if (!trimmed) continue;

    // If adding this sentence would exceed 500 chars and we already have content,
    // flush the current chunk and start fresh
    if (current.length + trimmed.length + (current ? 1 : 0) > 500 && current) {
      chunks.push(current.trim());
      current = trimmed;
    } else {
      current = current ? current + ' ' + trimmed : trimmed;
    }
  }

  // Push any remaining content
  if (current.trim()) {
    chunks.push(current.trim());
  }

  return chunks.filter(c => c.length > 0);
}

export { stripMarkdown, chunkText };
